// src/components/album/voice/VoiceRecorder.jsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  Mic, 
  Square, 
  Play, 
  Pause,
  Trash2, 
  Save, 
  X, 
  Loader,
  AlertCircle,
  Pin,
  Volume2
} from 'lucide-react';

export const VoiceRecorder = ({ 
  mediaItem = null,
  level = 'media', // 'album' | 'gallery' | 'media'
  onSave, 
  onCancel
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const [uploadMode, setUploadMode] = useState('record'); 
  const fileInputRef = useRef(null);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);

  const [speakerInfo, setSpeakerInfo] = useState({
    name: '',
    relation: '',
    isPinned: false
  });

  // Maximum recording duration (from settings or default to 60 seconds)
  const maxDuration = 120; // Can be passed as prop

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/m4a', 'audio/webm', 'audio/ogg'];
    if (!validTypes.some(type => file.type.includes(type.split('/')[1]))) {
      setError('Please upload a valid audio file (MP3, WAV, M4A, WebM, or OGG)');
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size must be less than 50MB');
      return;
    }

    setError(null);
    setAudioBlob(file);
    
    // Get audio duration
    const audio = new Audio(URL.createObjectURL(file));
    audio.onloadedmetadata = () => {
      setDuration(Math.floor(audio.duration));
    };
  };

  const startRecording = async () => {
    try {
      setError(null);
      
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      streamRef.current = stream;
      
      // Create MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : 'audio/mp4';
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      
      // Handle data available
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      // Handle recording stop
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setDuration(recordingTime);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };
      
      // Start recording
      mediaRecorderRef.current.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          
          // Auto-stop at max duration
          if (newTime >= maxDuration) {
            stopRecording();
            return maxDuration;
          }
          
          return newTime;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      
      if (error.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else {
        setError('Could not access microphone. Please check your device settings.');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current) {
      // Create audio element for preview
      audioRef.current = new Audio(URL.createObjectURL(audioBlob));
      audioRef.current.onended = () => setIsPlaying(false);
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleReRecord = () => {
    setAudioBlob(null);
    setDuration(0);
    setRecordingTime(0);
    setIsPlaying(false);
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  const handleSave = async () => {
    if (!audioBlob) {
      setError('No recording to save');
      return;
    }

    if (!speakerInfo.name.trim()) {
      setError('Please provide your name before saving');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave({
        audioBlob,
        duration,
        speaker: {
          name: speakerInfo.name.trim(),
          relation: speakerInfo.relation.trim(),
          isPinned: speakerInfo.isPinned
        },
        transcript: '', // Can add transcription here if available
        language: 'en'
      });
    } catch (error) {
      console.error('Error saving voice note:', error);
      setError('Failed to save voice note. Please try again.');
      setIsSaving(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTitle = () => {
    switch (level) {
      case 'album': return 'Record Album Story';
      case 'gallery': return 'Record Gallery Story';
      case 'media': return 'Record Voice Note';
      default: return 'Record Voice';
    }
  };

  const getDescription = () => {
    switch (level) {
      case 'album': return 'Add a voice introduction to welcome visitors to your album';
      case 'gallery': return 'Narrate the story behind this gallery';
      case 'media': return 'Share the story behind this photo';
      default: return 'Record your voice';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white bg-opacity-20 backdrop-blur-sm flex items-center justify-center">
                <Mic className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">{getTitle()}</h3>
                <p className="text-sm text-purple-100">{getDescription()}</p>
              </div>
            </div>
            <button 
              onClick={onCancel} 
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Recording UI */}
        <div className="p-6 space-y-4 ">
          {/* Mode Selector */}
          {!audioBlob && !isRecording && (
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => setUploadMode('record')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
                  uploadMode === 'record'
                    ? 'bg-white text-purple-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Mic className="w-4 h-4" />
                Record
              </button>
              <button
                onClick={() => setUploadMode('upload')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${
                  uploadMode === 'upload'
                    ? 'bg-white text-purple-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Volume2 className="w-4 h-4" />
                Upload
              </button>
            </div>
          )}

          {/* Recording Mode */}
          {uploadMode === 'record' && !audioBlob && (
            <div className="flex flex-col items-center justify-center space-y-6 py-8">
              {/* Error Display */}
              {error && (
                <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Recording Animation */}
              <div className="relative">
                <div className={`w-32 h-32 rounded-full flex items-center justify-center ${
                  isRecording 
                    ? 'bg-red-100 animate-pulse' 
                    : 'bg-gradient-to-br from-purple-100 to-pink-100'
                }`}>
                  <div className={`w-24 h-24 rounded-full flex items-center justify-center ${
                    isRecording ? 'bg-red-200' : 'bg-white'
                  } shadow-lg`}>
                    {isRecording ? (
                      <Square className="w-12 h-12 text-red-600 fill-current" />
                    ) : (
                      <Mic className="w-12 h-12 text-purple-600" />
                    )}
                  </div>
                </div>
                
                {/* Recording indicator dots */}
                {isRecording && (
                  <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full animate-ping" />
                )}
              </div>

              {/* Recording Time / Instructions */}
              {isRecording ? (
                <div className="text-center space-y-2">
                  <div className="text-4xl font-bold text-gray-900 tabular-nums">
                    {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                  </div>
                  <p className="text-sm text-gray-600">
                    Max duration: {Math.floor(maxDuration / 60)}:{(maxDuration % 60).toString().padStart(2, '0')}
                  </p>
                </div>
              ) : (
                <div className="text-center space-y-2">
                  <p className="text-lg font-medium text-gray-900">
                    Ready to record
                  </p>
                  <p className="text-sm text-gray-600">
                    Press the button below to start
                  </p>
                </div>
              )}

              {/* Record Button */}
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={error && !isRecording}
                className={`px-8 py-4 rounded-full font-semibold text-lg transition-all shadow-lg hover:shadow-xl transform hover:scale-105 ${
                  isRecording
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                }`}
              >
                {isRecording ? 'Stop Recording' : 'Start Recording'}
              </button>
            </div>
          )}

          {/* Upload Mode */}
          {uploadMode === 'upload' && !audioBlob && (
            <div className="flex flex-col items-center justify-center space-y-6 py-8">
              {/* Error Display */}
              {error && (
                <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Upload Icon */}
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center shadow-lg">
                  <Volume2 className="w-12 h-12 text-purple-600" />
                </div>
              </div>

              {/* Upload Instructions */}
              <div className="text-center space-y-2">
                <p className="text-lg font-medium text-gray-900">
                  Upload an audio file
                </p>
                <p className="text-sm text-gray-600">
                  MP3, WAV, M4A, WebM, or OGG (Max 50MB)
                </p>
              </div>

              {/* Upload Button */}
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-8 py-4 rounded-full font-semibold text-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Choose Audio File
              </button>
            </div>
          )}
          {/* Audio Preview & Speaker Info */}
          <div className="overflow-y-auto max-h-[50vh] px-2">
          {audioBlob && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 space-y-6">
              {/* Audio Player */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <button
                    onClick={handlePlayPause}
                    className="w-14 h-14 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white flex items-center justify-center transition-all shadow-md hover:shadow-lg transform hover:scale-105"
                  >
                    {isPlaying ? (
                      <Pause className="w-6 h-6 fill-current" />
                    ) : (
                      <Play className="w-6 h-6 fill-current ml-1" />
                    )}
                  </button>

                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {uploadMode === 'record' ? 'Your Recording' : 'Uploaded Audio'}
                      </span>
                      <span className="text-sm text-gray-600 tabular-nums">
                        {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all" 
                          style={{ width: '0%' }} />
                    </div>
                  </div>

                  <button
                    onClick={handleReRecord}
                    className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete and re-record"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Speaker Info Form */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={speakerInfo.name}
                    onChange={(e) => setSpeakerInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Sarah Johnson"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Relation (Optional)
                  </label>
                  <input
                    type="text"
                    value={speakerInfo.relation}
                    onChange={(e) => setSpeakerInfo(prev => ({ ...prev, relation: e.target.value }))}
                    placeholder="e.g., Grandmother, Friend, Photographer"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Pin as Featured (only for media level) */}
                {level === 'media' && (
                  <label className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={speakerInfo.isPinned}
                      onChange={(e) => setSpeakerInfo(prev => ({ ...prev, isPinned: e.target.checked }))}
                      className="w-5 h-5 rounded text-amber-600 focus:ring-amber-500 focus:ring-offset-0 cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Pin className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-gray-900">
                          Pin as featured story
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">
                        This voice note will be highlighted first
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Footer Actions - Sticky Bottom */}
        {audioBlob && (
          <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-6 shadow-lg z-10">
            <div className="flex gap-3">
              <button
                onClick={onCancel}
                className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !speakerInfo.name.trim()}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-center gap-2 font-medium"
              >
                {isSaving ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Save Voice Note
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Info Footer */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <p className="text-xs text-gray-600 text-center">
            💡 Tip: Speak clearly and in a quiet environment for best quality
          </p>
        </div>
      </div>
    </div>
  );
};